import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Resolve } from "@angular/router";
import { AnalyticsHeaderService, SummaryPanelService, SUrlService } from "@senior-gestao-pessoas/angular-components";
import { LocaleService } from "@seniorsistemas/angular-components";
import { user } from "@seniorsistemas/senior-platform-data";
import * as Highcharts from "highcharts";
import {forkJoin, Observable} from "rxjs";
import { catchError, map, switchMap, take, tap } from "rxjs/operators";
import { AnalyticsService } from "../../core/services/analytics.service";
import { HcmConfigService } from "../../core/services/hcm-config.service";
import { ModuleName } from "../../core/common/enums";
import {AnalyticsFeatureToggleService} from "../services/analytics-feature-toggle.service";

@Injectable()
export class LocaleResolver implements Resolve<any> {
	constructor(private localeService: LocaleService) {
	}

	resolve() {
		return this.localeService.get()
			.pipe(
				map((locale) => {
					if (locale && locale.number && locale.calendar) {
						Highcharts.setOptions({
							credits: {
								enabled: false
							},
							lang: {
								decimalPoint: locale.number.decimalSeparator,
								thousandsSep: locale.number.thousandsSeparator,
								shortMonths: locale.calendar.monthNamesShort,
								months: locale.calendar.monthNames
							}
						});
					}

					return locale;
				})
			);
	}
}

@Injectable()
export class EmployeeObjectOfAnalysisResolver implements Resolve<any> {
	constructor(private summaryPanelService: SummaryPanelService,
		private headerService: AnalyticsHeaderService,
		private analyticsService: AnalyticsService) {
	}

	resolve(route: ActivatedRouteSnapshot) {
		const employeeId = route.params['employeeId'] ? route.params['employeeId'] : this.headerService.getActiveEmployee();
		localStorage.removeItem('userRoleActive');
		return this.summaryPanelService.getEmployeeObjectOfAnalysis(employeeId)
			.pipe(
				take(1),
				switchMap((employee: any) => {
					return this.analyticsService.getUserRoleActiveBasedOnContext(route.data.analyticType, this.headerService.getActiveEmployee());
				}),
				take(1),
				map((userRole: any) => {
					if (userRole && userRole.id) {
						localStorage.setItem('userRoleActive', userRole.id);
					}
				})
			);
	}
}

@Injectable()
export class AnalyticsManagerUrlHCMResolver {
	constructor(private sUrlService: SUrlService) {
	}

	public resolve() {
		return this.sUrlService.getHcmFrontendURL();
	}
}

@Injectable()
export class UserDataResolver implements Resolve<any> {
	constructor() {
	}

	public resolve() {
		return user.getUserData();
	}
}

@Injectable()
export class PersonActiveEmployeesResolver implements Resolve<any> {
	constructor(private headerService: AnalyticsHeaderService) {
	}

	resolve() {
		return this.headerService.getPersonActiveEmployees().pipe(map((employees) => {
			this.headerService.personActiveEmployeesSubject.next(employees);
		}));
	}
}

@Injectable()
export class HcmTenantConfigResolver implements Resolve<any> {
	constructor(private hcmConfigService: HcmConfigService) {
	}

	resolve() {
		return this.hcmConfigService.getTenantConfig();
	}
}

@Injectable()
export class RecruitmentPropertyIsUsingNewVacancyManagementResolver
	implements Resolve<any> {
	constructor(private analyticsService: AnalyticsService) {}

	resolve() {
		return this.analyticsService
			.checkModule(ModuleName.RECRUIT_MANAGEMENT)
			.pipe(
				switchMap(({ result: payloadResult }: any) => {
					if (payloadResult) {
						return this.analyticsService
							.getCanUseVacancyManagement()
							.pipe(
								tap(({ result }: any) => {
									this.analyticsService.isUsingNewVacancyManagement = result;
								})
							);
					} else {
						return this.throwError();
					}
				}),
				catchError(() => {
					return this.throwError();
				})
			);
	}

	private throwError(): Observable<boolean> {
		this.analyticsService.isUsingNewVacancyManagement = false;

		return Observable.of(false);
	}
}

@Injectable()
export class RemunerationTeamTabResolver implements Resolve<any> {
	constructor(
		private readonly featureToggleService: AnalyticsFeatureToggleService
	) {}

	resolve(): Observable<any> {
		return forkJoin({
			toggle: this.featureToggleService.isNewAnalyticsEnabled()
		});
	}
}
